/**
 * The single way client code sends an unauthenticated user to sign in.
 *
 * `/login` is server-rendered — the SPA must never be the thing answering
 * it. When something upstream breaks that (a missing ingress path, a bad
 * proxy rule), the shell gets served at /login, finds no cookie, and sends
 * the browser to /login again. No server hop, no backoff: the page reloads
 * as fast as it can render until the browser gives up.
 *
 * The guard here is that the SPA refuses to navigate to /login when it is
 * already sitting on /login. The misroute still has to be fixed, but it
 * surfaces as a page that does nothing instead of a spin.
 */

const LOGIN_PATH = '/login';

/** True when the SPA has been served at /login, which should never happen. */
function alreadyOnLoginPage(): boolean {
  return window.location.pathname === LOGIN_PATH;
}

/**
 * Navigate to the sign-in page, optionally returning to `dest` afterwards.
 *
 * Does nothing if the SPA is already rendering at /login — see above.
 */
export function redirectToLogin(dest?: string): void {
  if (alreadyOnLoginPage()) {
    console.error(
      '[Auth] SPA was served at /login — that path must be server-rendered. ' +
        'Suppressing the redirect to avoid a reload loop.',
    );
    return;
  }
  const target = dest ?? window.location.pathname + window.location.search;
  // A return_to of /login would bounce the user right back here after a
  // successful sign-in.
  const suffix = target && target !== LOGIN_PATH ? `?return_to=${encodeURIComponent(target)}` : '';
  window.location.href = LOGIN_PATH + suffix;
}
